import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Handshake, CalendarPlus } from "lucide-react";
import api, { apiError } from "@/lib/api";

const daysSince = (d) => {
  if (!d) return null;
  return Math.floor((new Date() - new Date(d)) / 86400000);
};

export default function ContactsPanel() {
  const [contacts, setContacts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", role: "" });

  const load = useCallback(() => {
    api.get("/contacts").then((r) => setContacts(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post("/contacts", form);
      setForm({ name: "", organization: "", email: "", phone: "", role: "" });
      setAdding(false);
      toast.success("Contact added");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const touch = async (c) => {
    await api.post(`/contacts/${c.id}/touch`);
    toast.success(`Touch logged for ${c.name}`);
    load();
  };

  const followup = async (c) => {
    await api.post(`/contacts/${c.id}/followup`);
    toast.success(`Follow-up queued for tomorrow`);
  };

  const del = async (c) => {
    await api.delete(`/contacts/${c.id}`);
    toast("Contact deleted");
    load();
  };

  return (
    <div data-testid="contacts-panel" className="soft-card mx-4 my-2 overflow-hidden">
      <div className="p-5 border-b border-border/60">
        {adding ? (
          <div className="space-y-2">
            <input data-testid="contact-name-input" className="tactical-input" placeholder="Name *" value={form.name} onChange={set("name")} />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="contact-org-input" className="tactical-input" placeholder="Organization" value={form.organization} onChange={set("organization")} />
              <input data-testid="contact-role-input" className="tactical-input" placeholder="Role" value={form.role} onChange={set("role")} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="contact-email-input" className="tactical-input" placeholder="Email" value={form.email} onChange={set("email")} />
              <input data-testid="contact-phone-input" className="tactical-input" placeholder="Phone" value={form.phone} onChange={set("phone")} />
            </div>
            <div className="flex gap-2">
              <button data-testid="contact-save-button" onClick={add} className="flex-1 h-11 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest tap-btn">Save Contact</button>
              <button data-testid="contact-cancel-button" onClick={() => setAdding(false)} className="h-11 px-4 border border-border text-muted-foreground text-xs uppercase tap-btn">Cancel</button>
            </div>
          </div>
        ) : (
          <button data-testid="add-contact-button" onClick={() => setAdding(true)} className="w-full h-11 border border-primary text-primary text-xs font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-1.5">
            <Plus size={14} />Add Contact
          </button>
        )}
        <p className="text-[10px] text-muted-foreground mt-2 uppercase tracking-widest">Sorted stalest first — keep relationships warm</p>
      </div>
      <div className="divide-y divide-border/60">
        {contacts.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No contacts yet.</p>}
        {contacts.map((c) => {
          const ds = daysSince(c.last_touched);
          return (
            <div key={c.id} data-testid={`contact-row-${c.id}`} className="px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{[c.role, c.organization].filter(Boolean).join(" · ")}</p>
                  {(c.email || c.phone) && <p className="text-[10px] font-mono text-muted-foreground truncate">{[c.email, c.phone].filter(Boolean).join(" · ")}</p>}
                </div>
                <span className={`shrink-0 text-[10px] font-mono border px-1.5 py-0.5 rounded-md ${ds === null ? "border-destructive/60 text-destructive" : ds > 14 ? "border-[#D9A441] text-[#9a7423]" : "border-border text-muted-foreground"}`}>
                  {ds === null ? "NEVER TOUCHED" : ds === 0 ? "TODAY" : `${ds}D AGO`}
                </span>
              </div>
              <div className="flex gap-2 mt-2">
                <button data-testid={`contact-touch-${c.id}`} onClick={() => touch(c)} className="flex-1 h-9 border border-[#7A9B76] text-[#5f7f5b] text-[10px] font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-1"><Handshake size={12} />Log Touch</button>
                <button data-testid={`contact-followup-${c.id}`} onClick={() => followup(c)} className="flex-1 h-9 border border-border text-foreground text-[10px] font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-1"><CalendarPlus size={12} />Follow-up</button>
                <button data-testid={`contact-delete-${c.id}`} onClick={() => del(c)} className="h-9 px-3 border border-border text-destructive tap-btn"><Trash2 size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
